/* The scheduling vocabulary, in one place because three screens read it and none of them owns it.
 *
 * Connection detail renders a harvest schedule, Rules renders a binding's execution schedule and
 * its harvest trigger, and Executions renders which of the three caused a run. The outcome words,
 * the tones and the absence rules are the same on all of them — and they have to be, because
 * `requested` on a harvest card and `requested` on a binding card are the same fact about the same
 * kind of thing. Two copies is how one of them comes to call `deferred` a failure.
 *
 * Nothing here formats an instant. That is `whenLabel`'s job on the audit slice and the component's
 * choice; this file decides *meaning* — is this an absence, a decision, a condition, or a fault.
 */

import type { BadgeTone } from '@/shared/ui/StatusBadge';

/** The five outcomes the API can report, plus the absence that is not one of them.
 *
 * Derived from the generated `ExecutionSchedule['last_outcome']` union rather than restated, so a
 * sixth member added to the contract is a compile error here instead of a value that falls through
 * to a default tone.
 */
export type ScheduleOutcome = 'requested' | 'deferred' | 'refused' | 'failed' | 'skipped';

/** Outcome -> badge tone. **Total, with `satisfies`, and that is deliberate.**
 *
 * A `switch` with a `default` would render a sixth outcome in whichever tone the default happened
 * to be — and §9 records that exact defect on `core.ai.evals`' verdict map, where an unrecognised
 * value inherited the *permitted* tone. A total map makes the omission a compile error in the file
 * whose whole subject is that these five stay five.
 *
 * `deferred` is `info` and `skipped` is `neutral`, neither `warn`: a deferral means the
 * configuration is working and the moment was wrong, and a skip means the subject was switched off
 * or the harvest did not succeed. Colouring either amber would train a reader to ignore amber,
 * which is the one thing that makes `refused` and `failed` unreadable.
 */
export const OUTCOME_TONE = {
  requested: 'ok',
  deferred: 'info',
  refused: 'danger',
  failed: 'danger',
  skipped: 'neutral',
} as const satisfies Record<ScheduleOutcome, BadgeTone>;

/** The five, as a set, so a value off the wire can be checked against them rather than cast. */
const OUTCOMES: readonly ScheduleOutcome[] = [
  'requested',
  'deferred',
  'refused',
  'failed',
  'skipped',
];

/** Narrow `last_outcome` from the wire. **A check, not an assertion, and the difference matters.**
 *
 * The contract declares the five as an enum and the TypeScript generator emits `string | null` for
 * it — so `as ScheduleOutcome` would be an unchecked promise, and a sixth outcome from a server one
 * version ahead would reach `OUTCOME_TONE[value]` and index it with a key it does not have. That is
 * `undefined` in a class name and an unstyled chip, which reads as *no* outcome on the one field
 * whose whole job is to say what happened.
 *
 * An unrecognised value returns `null` — the same as an absent one, which is the safe direction:
 * "nothing has happened yet" is a stated absence, where a blank chip is a broken render.
 */
export function asOutcome(value: string | null | undefined): ScheduleOutcome | null {
  if (value === null || value === undefined) {
    return null;
  }
  return OUTCOMES.find((each) => each === value) ?? null;
}

/** Whether a human should look. **`refused` and `failed` only.**
 *
 * The server sends `needs_attention` and this exists to agree with it rather than to replace it:
 * a screen deriving its own answer is how the two come to disagree about which rows are urgent.
 * Used where the payload is absent — a compact cell that has an outcome and no envelope.
 */
export function needsAttention(outcome: ScheduleOutcome | null): boolean {
  return outcome === 'refused' || outcome === 'failed';
}

/** How the four scheduling surfaces describe themselves when nothing has happened yet.
 *
 * `null` is not an outcome. A schedule that has never come due, a trigger no harvest has reached,
 * and a run that found nothing are three different facts, and rendering any of them as a zero or a
 * dash alone is the absence-is-not-zero rule this platform holds at ten sites.
 */
export type Presence =
  /** Nobody has configured one. An invitation, not a fault. */
  | { readonly kind: 'unconfigured' }
  /** Stored and switched off. Somebody's decision, and reversible. */
  | { readonly kind: 'disabled' }
  /** On, and no occurrence has been computed yet — the sweep has not reached it. */
  | { readonly kind: 'unprimed' }
  /** On, with a next occurrence. */
  | { readonly kind: 'armed' }
  /** Paused itself after consecutive refusals. A condition; it lapses on its own. */
  | { readonly kind: 'suspended'; readonly until: string; readonly reason: string };

/** What a schedule payload amounts to, in one read.
 *
 * **Suspension is tested before `enabled`**, because a suspended schedule is still enabled and the
 * two say different things to a reader: disabled asks them to re-enable it, suspended asks them to
 * fix whatever caused the refusals and wait. Reversing the order would hide every suspension behind
 * "on".
 */
export function presenceOf(schedule: {
  readonly configured: boolean;
  readonly enabled: boolean;
  readonly next_run_at?: string | null;
  readonly suspended?: boolean;
  readonly suspended_until?: string | null;
  readonly suspension_reason?: string | null;
}): Presence {
  if (!schedule.configured) {
    return { kind: 'unconfigured' };
  }
  if (
    schedule.suspended === true &&
    typeof schedule.suspended_until === 'string' &&
    typeof schedule.suspension_reason === 'string'
  ) {
    return {
      kind: 'suspended',
      until: schedule.suspended_until,
      reason: schedule.suspension_reason,
    };
  }
  if (!schedule.enabled) {
    return { kind: 'disabled' };
  }
  /* `next_run_at` is null for an enabled schedule no sweep has primed. Distinct from disabled,
     which the branch above already took: one is waiting, the other is off. */
  return schedule.next_run_at === null || schedule.next_run_at === undefined
    ? { kind: 'unprimed' }
    : { kind: 'armed' };
}

/** Monday 0 through Sunday 6 — `datetime.weekday()`'s numbering, which the API uses.
 *
 * Spelled out rather than derived from `Intl`, because the *order* is the API's and a locale-first
 * week would put Sunday at index 0 for an American reader and silently send the wrong day.
 */
export const WEEKDAY_ORDER: readonly number[] = [0, 1, 2, 3, 4, 5, 6];

/** The copy key for one weekday, so the labels are translatable rather than hardcoded English. */
export function weekdayKey(day: number): string {
  return `scheduling.weekday.${String(day)}`;
}

/** Whether a weekday set covers the whole week, which is what `daily` requires.
 *
 * Used to render "Every day" instead of listing seven abbreviations — the design's wording, and
 * the more readable answer.
 */
export function coversEveryDay(weekdays: readonly number[] | undefined): boolean {
  return weekdays !== undefined && new Set(weekdays).size === WEEKDAY_ORDER.length;
}

/** The zones offered in the picker.
 *
 * **A list rather than a free-text field, and that is the validation.** `UTC+05:30` is refused by
 * the domain and by a CHECK constraint because an offset cannot express a daylight-saving
 * transition — so the control must not be able to produce one. A short list of the zones this
 * platform's market actually runs in, plus UTC; a deployment needing another adds it here rather
 * than typing one at 2am.
 *
 * `Intl.supportedValuesOf('timeZone')` would give hundreds and is deliberately not used: a
 * six-hundred-item select is a worse control, and the browser's list is not the same as the
 * server's `tzdata`, so it can offer a name the API refuses.
 */
export const TIMEZONES: readonly string[] = [
  'Asia/Kolkata',
  'Asia/Dubai',
  'Asia/Singapore',
  'Europe/London',
  'Europe/Frankfurt',
  'America/New_York',
  'UTC',
];

/** `HH:MM`, 24-hour, and nothing else.
 *
 * The same bound `local_time_from` applies on the server: seconds and offset suffixes are refused
 * because the zone is a separate field. Checked here so a reader is told before a round trip, not
 * instead of it — the server still refuses.
 */
export const LOCAL_TIME_PATTERN = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;

export function isLocalTime(value: string): boolean {
  return LOCAL_TIME_PATTERN.test(value.trim());
}

/* ------------------------------------------------------------------ instants, as a reader reads */

/* Moved to `@/shared/model/instant` when the catalogue's source card needed the same rendering.
 *
 * Re-exported rather than repointed at every call site: `ScheduleParts`, `BindingScheduling` and
 * this module's own tests all name it here, and a move that also edits five importers is a move
 * whose behaviour nobody can check against the tests that were already passing.
 */
export { formatInstant } from '@/shared/model/instant';

/** The two strategies a schedule or a trigger may carry. **`incremental` is absent.**
 *
 * It is a real `ExecutionStrategy` the platform refuses to *schedule*, because it renders
 * identically to `full` today — so offering it would let somebody configure a nightly run believing
 * it reads only new rows while it scans whole tables. The server answers 422; this list is what
 * stops the control ever producing one.
 */
export const SCHEDULABLE_STRATEGIES: readonly ['full', 'sampled'] = ['full', 'sampled'];

export const SEVERITY_FLOORS: readonly ['blocker', 'error', 'warning', 'info'] = [
  'blocker',
  'error',
  'warning',
  'info',
];

/** Who caused a run, from `Execution.requested_by`.
 *
 * Four values because there are four different follow-ups. `unknown` is not a defensive branch: a
 * `system:` actor this build does not recognise is a sweep added in a later release, and reading it
 * as a person would put a colleague's name on a scan nobody ran.
 */
export type RunSource = 'manual' | 'scheduled' | 'after_harvest' | 'unknown';

const SCHEDULER_ACTOR = 'system:rule-scheduler';
const FOLLOWER_ACTOR = 'system:harvest-follower';

/** Classify a run's origin. **A person is the fallback only for a non-`system:` subject.**
 *
 * The two unattended actors are matched exactly rather than by prefix, so a third sweep lands in
 * `unknown` and renders as "not reported" instead of being mislabelled as one of these two. Any
 * other `system:` subject is also `unknown` for the same reason — `isSystemActor` on the audit
 * slice draws the same line and this agrees with it deliberately.
 */
export function sourceOf(requestedBy: string | undefined): RunSource {
  if (requestedBy === undefined || requestedBy.trim() === '') {
    return 'unknown';
  }
  if (requestedBy === SCHEDULER_ACTOR) {
    return 'scheduled';
  }
  if (requestedBy === FOLLOWER_ACTOR) {
    return 'after_harvest';
  }
  return requestedBy.startsWith('system:') ? 'unknown' : 'manual';
}

/** The filter options on Executions, in the design's order. `all` first because it is the default.
 */
export const SOURCE_FILTERS: readonly ['all', 'manual', 'scheduled', 'after_harvest'] = [
  'all',
  'manual',
  'scheduled',
  'after_harvest',
];

export type SourceFilter = (typeof SOURCE_FILTERS)[number];

export function matchesSource(
  requestedBy: string | undefined,
  filter: SourceFilter,
): boolean {
  return filter === 'all' || sourceOf(requestedBy) === filter;
}

/* ------------------------------------------------------------------ scheduler durability */

/** The two scheduling capabilities `/readiness` can report on, by their server-side names. */
export const SCHEDULER_CAPABILITIES: readonly [
  'harvest_scheduler',
  'rule_execution_scheduler',
] = ['harvest_scheduler', 'rule_execution_scheduler'];

export type SchedulerFidelity = 'production' | 'stub' | 'missing' | 'unknown';

export interface SchedulerHealth {
  readonly capability: (typeof SCHEDULER_CAPABILITIES)[number];
  readonly fidelity: SchedulerFidelity;
  /** The server's own sentence, which carries the operator's next step. Empty when production. */
  readonly detail: string;
}

/** What a readiness response says about the two schedulers.
 *
 * **`/readiness` returns only the *degraded* capabilities, so `production` is an absence** — and
 * that is the one thing about this shape worth stating, because reading it the other way round
 * would report a healthy deployment as unknown on every screen. A capability missing from the list
 * is answering; one in it carries its own fidelity and its own detail.
 *
 * `unknown` is for a readiness response that could not be read at all, and it is deliberately not
 * folded into `production`: "nothing is degraded" and "we could not ask" are different facts, and
 * the second must not render as a green tick on the surface an operator checks durability from.
 */
export function schedulerHealth(
  degraded:
    | readonly {
        readonly capability: string;
        readonly fidelity: string;
        readonly detail: string;
      }[]
    | null,
): readonly SchedulerHealth[] {
  return SCHEDULER_CAPABILITIES.map((capability) => {
    if (degraded === null) {
      return { capability, fidelity: 'unknown' as const, detail: '' };
    }
    const entry = degraded.find((each) => each.capability === capability);
    if (entry === undefined) {
      return { capability, fidelity: 'production' as const, detail: '' };
    }
    return {
      capability,
      fidelity: entry.fidelity === 'missing' ? ('missing' as const) : ('stub' as const),
      detail: entry.detail,
    };
  });
}

/** Whether scheduled work on this deployment survives a restart of the replica that accepted it.
 *
 * **`production` only.** A stub schedules and fires in process — nothing is lost, and saying
 * "scheduling is off" would send a reader to the wrong place — but the in-flight timer does not
 * survive a restart, which is exactly what the card's notice says. `unknown` is not durable
 * either: a screen cannot promise durability it could not verify.
 */
export function isDurable(
  health: readonly SchedulerHealth[],
  capability: string,
): boolean {
  return health.find((each) => each.capability === capability)?.fidelity === 'production';
}

/* ------------------------------------------------------------------ the recurrence, as a value */

/** A recurrence as the two forms hold it while a reader is editing.
 *
 * Camel-cased and flat, deliberately unlike the wire shape: the API's `local_time` / `weekdays`
 * belong to a request body, and a form binding directly to that shape ends up sending whatever it
 * happens to hold. The mapping is one function in each direction and both are here.
 */
export interface RecurrenceDraft {
  readonly cadence: 'daily' | 'weekly';
  readonly localTime: string;
  readonly timezone: string;
  readonly weekdays: readonly number[];
}

/** A fresh recurrence for a subject that has none.
 *
 * **02:00 in the deployment's first offered zone, and deliberately not the current hour rounded
 * up.** A schedule defaulting to now is one that runs during the working day, and the whole point
 * of unattended work is that it does not. The reader still chooses; this is where the form starts.
 */
export function defaultRecurrence(): RecurrenceDraft {
  return {
    cadence: 'daily',
    localTime: '02:00',
    timezone: TIMEZONES[0] ?? 'UTC',
    weekdays: WEEKDAY_ORDER,
  };
}

/** A stored payload as form values, falling back to the default for anything absent.
 *
 * A `configured: false` payload carries none of these — every recurrence field is optional on the
 * wire — so every one needs a fallback. Reading `stored.cadence!` would put `undefined` into a
 * select and leave the control blank with no clue why.
 */
export function recurrenceOf(stored: {
  readonly cadence?: 'daily' | 'weekly' | undefined;
  readonly local_time?: string | undefined;
  readonly timezone?: string | undefined;
  readonly weekdays?: readonly number[] | undefined;
}): RecurrenceDraft {
  const base = defaultRecurrence();
  return {
    cadence: stored.cadence ?? base.cadence,
    localTime: stored.local_time ?? base.localTime,
    timezone: stored.timezone ?? base.timezone,
    weekdays: stored.weekdays ?? base.weekdays,
  };
}

/** Whether a recurrence can be submitted, and the copy key saying why not. `null` means valid.
 *
 * Exported so the submit button and its hint read the same answer — a button computing its own
 * `disabled` is how the two come to disagree, and a reader then sees an enabled control that does
 * nothing.
 *
 * **These are the bounds the server holds, checked here so a reader hears first — not instead.**
 * `local_time_from` refuses anything but `HH:MM`, and `Recurrence` refuses an empty weekday set.
 * Every one of them still refuses if this is wrong.
 */
export function recurrenceProblem(value: RecurrenceDraft): string | null {
  if (!isLocalTime(value.localTime)) {
    return 'scheduling.form.invalid.time';
  }
  if (value.cadence === 'weekly' && value.weekdays.length === 0) {
    return 'scheduling.form.invalid.noWeekdays';
  }
  return null;
}

/** A daily cadence covers all seven days — the domain requires it, so the control enforces it.
 *
 * Switching to `daily` selects every day and the weekday control disappears rather than being
 * shown greyed with seven ticks: a disabled control a reader cannot change is one that invites
 * them to try. Switching to `weekly` keeps whatever was selected, so daily -> weekly -> daily
 * loses nothing.
 */
export function withCadence(
  value: RecurrenceDraft,
  cadence: 'daily' | 'weekly',
): RecurrenceDraft {
  return cadence === 'daily'
    ? { ...value, cadence, weekdays: WEEKDAY_ORDER }
    : { ...value, cadence };
}
